import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Clock, Loader2, MapPin, Phone, Share2, Users } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function RouteLanding() {
  const { companyId, slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const load = useCallback(() => {
    api.get(`/public/company/${companyId}/route/${slug}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(errMsg(e)));
  }, [companyId, slug]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.route.origin} to ${data.route.airport} · ${money(data.route.price)} · ${data.company.name}`;
  }, [data]);

  const share = async () => {
    const url = window.location.href;
    const text = `${data.route.origin} → ${data.route.airport} · ${money(data.route.price)} flat with ${data.company.name}`;
    try {
      if (navigator.share) await navigator.share({ title: text, text, url });
      else { await navigator.clipboard.writeText(url); setCopied("Link copied — paste it anywhere."); }
    } catch { setCopied(url); }
  };

  const book = () => {
    const q = new URLSearchParams({ pickup: data.route.origin, dropoff: data.route.airport });
    navigate(`/book/${data.company.handle || companyId}?${q.toString()}`);
  };

  if (error) {
    return (
      <div data-testid="route-error" className="flex min-h-screen items-center justify-center bg-ink-900 p-6 text-center">
        <div>
          <p className="text-sm text-red-400">{error}</p>
          <Button data-testid="route-book-anyway" onClick={() => navigate(`/book/${companyId}`)}
            className="mt-5 bg-gold text-ink-900 hover:bg-gold-light">Book a ride</Button>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-ink-900"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  }

  const { route, company, vehicles } = data;
  const photo = vehicles.find((v) => (v.photos || []).length)?.photos?.[0];

  return (
    <div data-testid="route-landing" className="min-h-screen bg-ink-900">
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-16">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{company.name}</p>
        <h1 data-testid="route-title" className="mt-4 font-display text-4xl leading-tight text-slate-50 sm:text-5xl">
          {route.origin} to {route.airport}
        </h1>
        <p className="mt-4 text-base text-slate-400">
          One flat price, booked online, with a professional chauffeur. No surge, no meter.
        </p>

        <div className="mt-8 flex flex-wrap items-end gap-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Flat rate from</p>
            <p data-testid="route-price" className="font-display text-5xl text-gold">{money(route.price)}</p>
          </div>
          {route.minutes != null && (
            <p data-testid="route-drive-time" className="flex items-center gap-2 text-sm text-slate-300">
              <Clock className="h-4 w-4 text-gold" /> about {route.minutes} min drive
            </p>
          )}
          {route.miles != null && (
            <p data-testid="route-miles" className="flex items-center gap-2 text-sm text-slate-300">
              <MapPin className="h-4 w-4 text-gold" /> {route.miles} miles
            </p>
          )}
        </div>

        <div className="mt-9 flex flex-wrap gap-3">
          <Button data-testid="route-book-btn" onClick={book}
            className="h-12 bg-gold px-8 text-ink-900 hover:bg-gold-light">
            Book this trip
          </Button>
          <Button data-testid="route-share-btn" variant="outline" onClick={share}
            className="h-12 border-ink-600 bg-transparent px-6 text-slate-200 hover:text-gold">
            <Share2 className="mr-2 h-4 w-4" /> Share
          </Button>
          {company.phone && (
            <a data-testid="route-call" href={`tel:${company.phone}`}
              className="flex h-12 items-center gap-2 rounded-md border border-ink-600 px-6 text-sm text-slate-200 hover:text-gold">
              <Phone className="h-4 w-4" /> {company.phone}
            </a>
          )}
        </div>
        {copied && <p data-testid="route-copied" className="mt-3 text-sm text-emerald-400">{copied}</p>}

        {photo && (
          <img data-testid="route-photo" src={photo} alt={`${company.name} vehicle`}
            className="mt-10 h-64 w-full rounded-xl border border-ink-600 object-cover" />
        )}

        {vehicles.length > 0 && (
          <div className="mt-10" data-testid="route-vehicles">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Available on this route</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {vehicles.slice(0, 6).map((v) => (
                <div key={v.id} className="rounded-xl border border-ink-600 bg-ink-800 p-4">
                  <p className="text-sm text-slate-100">{v.name}</p>
                  {v.capacity > 0 && (
                    <p className="mt-1 flex items-center gap-2 font-mono text-[11px] text-slate-500">
                      <Users className="h-3 w-3" /> up to {v.capacity} passengers
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-12 text-xs text-slate-600">
          Price shown is the flat rate for this route. Tolls, extra stops, waiting time and gratuity
          may apply. Confirm the final total at checkout.
        </p>
      </div>
    </div>
  );
}
